import { useCallback, useRef } from "react";

import { type ThrottleOptions, useThrottleFn } from "./useThrottle";

/**
 * A promise-aware version of useThrottleFn.
 * Wraps lodash throttle to return Promises that resolve when execution completes.
 * Multiple calls within the throttle window share the same execution and all resolve together.
 */
export function useThrottleFnAsync<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  wait: number,
  options?: ThrottleOptions,
) {
  // Queue of promises waiting for the throttled function to complete
  const pendingPromises = useRef<
    Array<{ reject: (error: unknown) => void; resolve: (value: Awaited<ReturnType<T>>) => void }>
  >([]);

  // Wrapper that resolves/rejects all queued promises when fn completes
  const wrappedFn = async (...args: Parameters<T>) => {
    try {
      const result = await fn(...args);
      pendingPromises.current.forEach((p) => p.resolve(result as Awaited<ReturnType<T>>));
      pendingPromises.current = [];
      return result;
    } catch (error) {
      pendingPromises.current.forEach((p) => p.reject(error));
      pendingPromises.current = [];
      throw error;
    }
  };

  const throttled = useThrottleFn(wrappedFn, wait, options);

  // Return a function that queues a promise and triggers the throttled execution
  const throttledAsync = useCallback(
    (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
      return new Promise((resolve, reject) => {
        pendingPromises.current.push({ reject, resolve });
        void throttled(...args);
      });
    },
    [throttled],
  );

  return throttledAsync;
}
